import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Download, LogOut, Activity, Smartphone, MessageSquare, User, ChevronDown, Settings, HelpCircle, MessageCircle } from 'lucide-react';
import TraqPackMap from './components/TraqPackMap';
import TimeFilter from './components/TimeFilter';
import StatsGrid from './components/StatsGrid';
import DataTable from './components/DataTable';
import LoginWithSignup from './components/LoginWithSignup';
import EditDeviceModal from './components/EditDeviceModal';
import DeviceSelector from './components/DeviceSelector';
import DevicesPage from './components/DevicesPage';
import DevicesPageV2 from './components/DevicesPageV2';
import DeviceStats from './components/DeviceStats';
import { AIQueryBar } from './components/AIQueryBar';
import AIChatPanel from './components/AIChatPanel';
import { supabase, traqPackAPI, GpsData, Device } from './lib/supabase';
import { useSupabaseAuth } from './contexts/SupabaseAuthContext';
import './App.css';

function AppWarehouse() {
  const { isAuthenticated, signOut, user } = useSupabaseAuth();
  const [activeTab, setActiveTab] = useState<'tracking' | 'devices'>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check if we should show devices tab
    if (urlParams.get('newDeviceManager') === 'true' || urlParams.get('tab') === 'devices') {
      return 'devices';
    }
    return 'tracking';
  });
  const [timeFilter, setTimeFilter] = useState<string>('1d');
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [gpsData, setGpsData] = useState<GpsData[]>([]);
  // Alerts removed for admin view - admins manage alerts separately
  const [devices, setDevices] = useState<Device[]>([]);
  const [stats, setStats] = useState({
    totalDevices: 0,
    activeDevices: 0,
    totalReadings: 0,
    recentReadings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [searchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDeviceForEdit, setSelectedDeviceForEdit] = useState<Device | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const useNewDevicePage = (() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('newDeviceManager') === 'true';
  })();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<{ full_name: string; email: string } | null>(null);

  // Close user menu when clicking outside (with delay to allow button clicks)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking on the menu or its children
      if (userMenuOpen && !target.closest('[data-user-menu]')) {
        setTimeout(() => setUserMenuOpen(false), 100);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Check if user is super admin and fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('is_super_admin, full_name, email')
          .eq('id', user.id)
          .single();
        
        if (data && !error) {
          setIsSuperAdmin(data.is_super_admin || false);
          setUserProfile({
            full_name: data.full_name || '',
            email: data.email || user.email || ''
          });
        } else if (error?.code === 'PGRST116') {
          // Profile doesn't exist, create one
          const newProfile = {
            id: user.id,
            email: user.email || '',
            full_name: user.user_metadata?.full_name || '',
            is_super_admin: false
          };
          
          const { data: createdProfile } = await supabase
            .from('user_profiles')
            .insert(newProfile)
            .select()
            .single();
          
          if (createdProfile) {
            setUserProfile({
              full_name: createdProfile.full_name || '',
              email: createdProfile.email || user.email || ''
            });
          }
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);

  const loadData = useCallback(async () => {
    if (!user?.id) {
      setDevices([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      console.log('Loading data for user:', user.email, 'isSuperAdmin:', isSuperAdmin);
      console.log('Selected device:', selectedDevice);
      
      // Use the traqPackAPI methods to fetch all data consistently
      const [gpsDataResult, devicesResult, statsResult] = await Promise.all([
        traqPackAPI.getGpsData(timeFilter, selectedDevice || undefined),
        traqPackAPI.getDevices(),
        traqPackAPI.getStats(),
      ]);

      console.log('Data loaded - Devices:', devicesResult.length, 'GPS:', gpsDataResult.length);
      
      setGpsData(gpsDataResult);
      setDevices(devicesResult);
      setStats(statsResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeFilter, selectedDevice, user, isSuperAdmin]);

  const refreshGpsData = useCallback(async () => {
    try {
      setGpsLoading(true);
      const gpsDataResult = await traqPackAPI.getGpsData(timeFilter, selectedDevice || undefined);
      setGpsData(gpsDataResult);
    } catch (error) {
      console.error('Error refreshing GPS data:', error);
    } finally {
      setGpsLoading(false);
    }
  }, [timeFilter, selectedDevice]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadData();
    }
  }, [isAuthenticated, user, loadData]);

  // Debug device access
  useEffect(() => {
    const debugDeviceAccess = async () => {
      if (user?.id) {
        try {
          const { data, error } = await supabase.rpc('debug_device_access');
          console.log('Debug device access:', data, error);
        } catch (err) {
          console.error('Debug RPC not available:', err);
        }
      }
    };
    debugDeviceAccess();
  }, [user]);

  const handleExportData = () => {
    const csvContent = [
      ['Device ID', 'Device Name', 'Latitude', 'Longitude', 'Battery %', 'Timestamp'],
      ...gpsData.map(data => [
        data.device_id,
        devices.find(d => d.id === data.device_id)?.device_nickname || 'Unknown',
        data.latitude,
        data.longitude,
        data.battery_percentage,
        new Date(data.created_at).toLocaleString(),
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traqpack-data-${new Date().toISOString()}.csv`;
    a.click();
  };

  const handleEditDevice = () => {
    if (selectedDevice) {
      const device = devices.find(d => d.id === selectedDevice);
      if (device) {
        setSelectedDeviceForEdit(device);
        setEditModalOpen(true);
      }
    }
  };

  const handleLogout = async () => {
    console.log('Logout clicked'); // Debug
    setUserMenuOpen(false); // Close menu first
    
    try {
      console.log('Calling signOut...'); // Debug
      await signOut();
      console.log('SignOut successful'); // Debug
      
      // Force a small delay to ensure state is updated, then redirect
      setTimeout(() => {
        if (isAuthenticated) {
          console.log('Still authenticated after signOut, forcing redirect');
          window.location.href = '/';
        }
      }, 100);
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect if logout fails
      window.location.href = '/';
    }
  };


  if (!isAuthenticated) {
    return <LoginWithSignup />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Clean Minimalist Header - Single Row */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-[2000]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Brand */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-traqpack-yellow rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">TraqPack</h1>
                <p className="text-xs text-gray-500">Real-time GPS Tracking Platform</p>
              </div>
            </div>

            {/* Navigation Tabs - Center */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => {
                  setActiveTab('tracking');
                  // Clear URL params when going back to tracking
                  const url = new URL(window.location.href);
                  url.searchParams.delete('newDeviceManager');
                  url.searchParams.delete('tab');
                  window.history.replaceState({}, '', url.toString());
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'tracking'
                    ? 'bg-traqpack-yellow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Activity className="w-4 h-4 inline mr-2" />
                Live Tracking
              </button>
              <button
                onClick={() => {
                  setActiveTab('devices');
                  // Update URL to reflect devices tab
                  const url = new URL(window.location.href);
                  url.searchParams.set('tab', 'devices');
                  window.history.replaceState({}, '', url.toString());
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'devices'
                    ? 'bg-traqpack-yellow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Smartphone className="w-4 h-4 inline mr-2" />
                Devices
              </button>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleExportData}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={gpsData.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              
              <button 
                onClick={() => setChatPanelOpen(!chatPanelOpen)}
                className="flex items-center px-3 py-2 text-sm bg-traqpack-yellow text-gray-900 hover:bg-yellow-400 rounded-lg transition-colors"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                AI Assistant
              </button>
              
              {/* User Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span>{userProfile?.full_name || user?.email?.split('@')[0] || 'My Account'}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {userMenuOpen && (
                  <div data-user-menu className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[3000]">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{userProfile?.full_name || user?.email?.split('@')[0] || 'User'}</div>
                          <div className="text-sm text-gray-500">{userProfile?.email || user?.email}</div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3">
                        <Settings className="w-4 h-4 text-gray-500" />
                        <span>Settings</span>
                      </button>
                      <button 
                        onClick={() => {
                          console.log('Help Center clicked - opening documentation');
                          window.open('https://docs.traqpack.com/getting-started-guide', '_blank', 'noopener,noreferrer');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <HelpCircle className="w-4 h-4 text-gray-500" />
                        <span>Help Center</span>
                      </button>
                      <a 
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=support@traqpack.com&su=Support%20Request%20-%20TraqPack"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <MessageCircle className="w-4 h-4 text-gray-500" />
                        <span>Support</span>
                      </a>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-100 pt-2">
                      <div 
                        onClick={(e) => {
                          console.log('Sign out button clicked!'); // Debug
                          e.preventDefault();
                          e.stopPropagation();
                          handleLogout();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 cursor-pointer"
                        role="button"
                        tabIndex={0}
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Zone 1: Top Area - Big Numbers (Not sticky - scrolls with page) */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <StatsGrid stats={stats} loading={loading} />
      </div>

      <main className="w-full">
        {activeTab === 'tracking' ? (
          <>
            {/* Zone 2 & 3: Main Content with Sidebar */}
            <div className="flex gap-4 flex-col lg:flex-row w-full px-2 py-6">
              {/* Zone 2: Left Sidebar */}
              <div className="w-full lg:w-72 xl:w-80 flex-shrink-0">
                {/* AI Assistant - Separate card */}
                <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">AI Assistant</h2>
                  <AIQueryBar onQueryResult={setGpsData} compact={true} />
                </div>
                
                {/* Filters & Controls */}
                <div className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Filters & Controls</h2>
                  
                  {/* Live Status */}
                  <div className="flex items-center space-x-2 bg-green-50 rounded-lg px-3 py-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-700">Live tracking</span>
                  </div>
                  
                  {/* Time Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Time Period</label>
                    <TimeFilter 
                      activeFilter={timeFilter} 
                      onFilterChange={setTimeFilter}
                      vertical={true}
                    />
                  </div>
                  
                  {/* Device Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Select Device</label>
                    <DeviceSelector
                      devices={devices}
                      selectedDevice={selectedDevice}
                      onSelectDevice={setSelectedDevice}
                      onEditDevice={handleEditDevice}
                    />
                  </div>
                  
                  {/* Device Stats */}
                  <DeviceStats 
                    device={devices.find(d => d.id === selectedDevice) || null}
                  />
                  
                  {/* Stats Summary */}
                  <div className="pt-6 border-t border-gray-200">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Active Devices</span>
                        <span className="text-sm font-semibold text-gray-900">{stats.activeDevices}/{stats.totalDevices}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total Readings</span>
                        <span className="text-sm font-semibold text-gray-900">{stats.totalReadings.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Recent Activity</span>
                        <span className="text-sm font-semibold text-gray-900">{stats.recentReadings.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Zone 3: Main Content Area */}
              <div className="flex-1 space-y-6">
              {/* Map Card */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold text-gray-900">Live Tracking Map</h2>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4" />
                    <span>{gpsData.length} locations</span>
                  </div>
                </div>
                  
                  <div className="relative bg-gray-50 rounded-lg overflow-hidden h-[625px] lg:h-[750px]">
                    <TraqPackMap 
                      gpsData={gpsData} 
                      timeFilter={timeFilter}
                      loading={loading}
                      selectedDevice={selectedDevice}
                    />
                  </div>
                </div>

              {/* Alerts removed for admin view - admins manage all user alerts separately */}
              
              {/* Data Table */}
              <DataTable 
                data={gpsData}
                devices={devices}
                loading={loading}
                searchTerm={searchTerm}
                onRefresh={refreshGpsData}
                refreshing={gpsLoading}
              />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full px-2 py-6">
            {useNewDevicePage ? <DevicesPageV2 /> : <DevicesPage />}
          </div>
        )}
      </main>

      {/* Modals */}
      <EditDeviceModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        device={selectedDeviceForEdit}
        onSave={async (deviceId, newName) => {
          // Prefer secure RPC that handles RLS and device ID normalization
          const { error } = await supabase.rpc('update_device_nickname', {
            p_device_id: deviceId,
            p_new_name: newName,
          });
          if (error) {
            console.warn('update_device_nickname RPC failed in AppWarehouse, trying API fallback:', error.message);
            try {
              await traqPackAPI.updateDevice(deviceId, newName);
            } catch (e: any) {
              console.error('Fallback update failed:', e);
              alert('Failed to save device name: ' + (e?.message || error.message));
              return;
            }
          }
          await loadData();
        }}
      />

      {/* AI Chat Panel */}
      <AIChatPanel
        isOpen={chatPanelOpen}
        onClose={() => setChatPanelOpen(false)}
      />
    </div>
  );
}

export default AppWarehouse;